import { useEffect, useMemo } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { routes } from "wasp/client/router";
import { useAuth } from "wasp/client/auth";
import { getGlobalConfig, useQuery } from "wasp/client/operations";
import "./Main.css";
import NavBar from "./components/NavBar/NavBar";
import {
  demoNavigationitems,
  marketingNavigationItems,
} from "./components/NavBar/constants";
import CookieConsentBanner from "./components/cookie-consent/Banner";
import { LocationProvider } from "./contexts/LocationContext";
import { ViewModeProvider } from "./contexts/ViewModeContext";
import { NotificationProvider } from "./contexts/NotificationContext";
import { Toaster } from "../components/ui/toaster";
import { ErrorBoundary } from "./components/ErrorBoundary";

/**
 * use this component to wrap all child components
 * this is useful for templates, themes, and context
 */
export default function App() {
  const location = useLocation();
  const navigate = useNavigate();
  const { data: user } = useAuth();
  const { data: config, isLoading: isConfigLoading } = useQuery(getGlobalConfig);

  const isMarketingPage = useMemo(() => {
    return (
      location.pathname === "/" || location.pathname.startsWith("/pricing")
    );
  }, [location]);

  const navigationItems = isMarketingPage
    ? marketingNavigationItems
    : demoNavigationitems;

  useEffect(() => {
    if (isConfigLoading) return;

    const isMaintenanceMode = config?.maintenanceMode;
    const isAdmin = user?.isAdmin;
    const isMaintenancePath = location.pathname === "/maintenance";
    const isLoginPath = location.pathname === "/login";
    const isAdminPath = location.pathname.startsWith("/admin");

    if (isMaintenanceMode && !isAdmin) {
      if (!isMaintenancePath && !isLoginPath) {
        navigate("/maintenance");
      }
    } else if (isMaintenancePath && (!isMaintenanceMode || isAdmin)) {
      navigate("/dashboard");
    }
  }, [config, user, location.pathname, isConfigLoading, navigate]);

  const shouldDisplayAppNavBar = useMemo(() => {
    const pathname = location.pathname;

    // Always hide NavBar on these specific routes
    if (
      pathname === routes.LoginRoute.build() ||
      pathname === routes.SignupRoute.build() ||
      pathname === routes.DemoSchedulerRoute.build() ||
      pathname === "/maintenance"
    ) {
      return false;
    }

    // Always hide NavBar on all location-based routes (starts with /locations/)
    if (pathname.startsWith("/locations/")) {
      return false;
    }

    // Hide NavBar on admin pages
    if (pathname.startsWith("/admin")) {
      return false;
    }

    // Hide NavBar on dashboard pages
    if (pathname.includes("/dashboard")) {
      return false;
    }

    // Legacy specific path checks (keeping for compatibility)
    if (pathname.includes("/tenants") || pathname.includes("/notices")) {
      return false;
    }

    return true;
  }, [location]);

  const isAdminDashboard = useMemo(() => {
    return location.pathname.startsWith("/admin");
  }, [location]);

  useEffect(() => {
    if (location.hash) {
      const id = location.hash.replace("#", "");
      const element = document.getElementById(id);
      if (element) {
        element.scrollIntoView();
      }
    }
  }, [location]);

  if (isConfigLoading) {
    return null; // Or a loading spinner if preferred
  }

  return (
    <ErrorBoundary>
      <LocationProvider>
        <ViewModeProvider>
          <NotificationProvider>
            <div className="min-h-screen bg-background text-foreground wasp-app-root">
              {isAdminDashboard ? (
                <Outlet />
              ) : (
                <>
                  {/* NavBar completely disabled for Gedoba location-based application */}
                  <div className="w-full">
                    <Outlet />
                  </div>
                </>
              )}
            </div>
            <CookieConsentBanner />
            <Toaster />
          </NotificationProvider>
        </ViewModeProvider>
      </LocationProvider>
    </ErrorBoundary>
  );
}
